// filepath: /work/workspace/be-a-veg/src/components/PickupEffects.jsx
import React, { useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { useWebSocket } from '../contexts/WebSocketContext.jsx';
import * as THREE from 'three';

function easeInCubic(t) { return t * t * t; }
function easeOutQuad(t) { return t * (2 - t); }

function BurstItem({ id, position = [0,0,0], color = '#00ffff', onDone }) {
  const groupRef = useRef();
  const started = useRef(Date.now());
  const duration = 350; // ms
  const shards = useMemo(() => {
    const arr = [];
    const n = 10;
    for (let i = 0; i < n; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI * 0.35; // mostly horizontal
      const speed = 1.5 + Math.random() * 1.5;
      arr.push({
        dir: new THREE.Vector3(
          Math.cos(theta) * Math.cos(phi),
          Math.sin(phi),
          Math.sin(theta) * Math.cos(phi)
        ).multiplyScalar(speed),
        size: 0.025 + Math.random() * 0.025,
      });
    }
    return arr;
  }, []);

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    const t = Math.min(1, (Date.now() - started.current) / duration);
    const k = easeOutQuad(t);
    // Update each child shard
    groupRef.current.children.forEach((child, i) => {
      const s = shards[i];
      if (!s) return;
      child.position.set(
        position[0] + s.dir.x * k,
        position[1] + s.dir.y * k,
        position[2] + s.dir.z * k
      );
      const scale = THREE.MathUtils.lerp(s.size, 0.0, t);
      child.scale.setScalar(scale);
      if (child.material) {
        child.material.opacity = 1 - t;
      }
    });
    if (t >= 1) onDone?.(id);
  });

  return (
    <group ref={groupRef}>
      {shards.map((s, idx) => (
        <mesh key={idx}>
          <sphereGeometry args={[1, 8, 8]} />
          <meshStandardMaterial color={color} emissive={color} emissiveIntensity={1.2} transparent opacity={1} />
        </mesh>
      ))}
    </group>
  );
}

function PickupEffectItem({ effect, getTargetPosition, color = '#00ffff', onDone }) {
  const meshRef = useRef();
  const lightRef = useRef();
  const start = useRef(effect.started || Date.now());
  const from = effect.from || [0, 1, 0];
  const duration = Math.max(200, effect.duration || 800);

  useFrame(() => {
    if (!meshRef.current) return;
    const now = Date.now();
    const t = Math.min(1, (now - start.current) / duration);
    const k = easeInCubic(t);

    const target = getTargetPosition();
    const fx = from[0], fy = from[1], fz = from[2];
    const tx = target[0], ty = target[1], tz = target[2];

    const x = THREE.MathUtils.lerp(fx, tx, k);
    const y = THREE.MathUtils.lerp(fy, ty, k);
    const z = THREE.MathUtils.lerp(fz, tz, k);

    meshRef.current.position.set(x, y, z);

    // Scale down as it approaches, with a subtle pulse
    const baseScale = THREE.MathUtils.lerp(0.25, 0.05, t);
    const pulse = 1 + Math.sin(t * Math.PI * 4) * 0.08;
    const s = baseScale * pulse;
    meshRef.current.scale.set(s, s, s);
    meshRef.current.rotation.y += 0.25;

    if (lightRef.current) {
      lightRef.current.position.set(x, y, z);
      lightRef.current.intensity = THREE.MathUtils.lerp(1.5, 0.0, t);
      lightRef.current.distance = 4;
    }

    if (t >= 1) {
      onDone?.(effect.id, [x, y, z]);
    }
  });

  return (
    <group>
      <mesh ref={meshRef} castShadow>
        <icosahedronGeometry args={[0.2, 0]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={2.0} metalness={0.4} roughness={0.2} />
      </mesh>
      <pointLight ref={lightRef} color={color} intensity={1.5} distance={4} />
    </group>
  );
}

export default function PickupEffects() {
  const { pickupEffects, removePickupEffect, worldState, pUUID } = useWebSocket();
  const players = worldState?.players || {};
  const [bursts, setBursts] = useState([]);

  const makeTargetGetter = (e) => () => {
    const st = players[e.toPUUID];
    if (st && Array.isArray(st.position) && st.position.length >= 3) {
      const scl = typeof st.scale === 'number' ? Math.max(0.1, Math.min(3, st.scale)) : 0.25;
      if (e.toPUUID === pUUID) {
        // Local player: aim at visible body center derived from BaseVegetableCharacter
        // groupY = position.y - 1 + scale
        // body center approx at groupY + 0.7*scale = position.y - 1 + 1.7*scale
        const y = st.position[1] - 1 + 1.7 * scl;
        return [st.position[0], y, st.position[2]];
      }
      // Remote players: account for the -1 offset used by OtherPlayer rendering, aim near chest
      const baseY = st.position[1] - 1;
      const y = baseY + 0.25 + 0.35 * scl;
      return [st.position[0], y, st.position[2]];
    }
    // Fallback: aim slightly above the original diamond from-position
    return [e.from[0], Math.max(0.5, e.from[1] + 0.2), e.from[2]];
  };

  const handleEffectDone = (id, pos) => {
    removePickupEffect(id);
    // Spawn a short burst at the final position
    const burst = { id: `${id}-burst`, position: pos || [0,0,0], color: '#00ffff' };
    setBursts(prev => [...prev, burst]);
  };

  const handleBurstDone = (id) => {
    setBursts(prev => prev.filter(b => b.id !== id));
  };

  if ((!pickupEffects || pickupEffects.length === 0) && (!bursts || bursts.length === 0)) return null;

  return (
    <group>
      {(pickupEffects || []).map((e) => (
        <PickupEffectItem
          key={e.id}
          effect={e}
          getTargetPosition={makeTargetGetter(e)}
          color={e.color || '#00ffff'}
          onDone={handleEffectDone}
        />
      ))}
      {(bursts || []).map(b => (
        <BurstItem key={b.id} id={b.id} position={b.position} color={b.color} onDone={handleBurstDone} />
      ))}
    </group>
  );
}
